import {
  RangeCalendar as AriaRangeCalendar,
  RangeCalendarProps as AriaRangeCalendarProps,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  DateValue,
  Text,
} from "react-aria-components";
import { focusRing } from "../../utils";
import { CalendarGridHeader, CalendarHeader } from "../Calendar";

/**
 * RangeCalendar
 *
 * Calendar allowing selection of a date range.
 */
export interface RangeCalendarProps<T extends DateValue> extends Omit<AriaRangeCalendarProps<T>, "visibleDuration"> {
  errorMessage?: string;
}

function getCellClassName({
  selectionState,
  isDisabled,
}: {
  selectionState: "none" | "middle" | "cap";
  isDisabled?: boolean;
}) {
  const baseClasses = `${focusRing} w-full h-full flex items-center justify-center rounded-sm forced-color-adjust-none font-semibold`;

  const selectionStateClasses = {
    none: "text-text-body group-hover:bg-background-muted group-pressed:bg-background-muted",
    middle: [
      "bg-gray-100 dark:bg-zinc-700 group-hover:bg-gray-200 dark:group-hover:bg-zinc-600 forced-colors:group-hover:bg-[Highlight]",
      "group-invalid:group-hover:bg-red-200 forced-colors:group-invalid:group-hover:bg-[Mark]",
      "group-pressed:bg-gray-200 dark:group-pressed:bg-zinc-600 forced-colors:group-pressed:bg-[Highlight] forced-colors:text-[HighlightText]",
      "group-invalid:group-pressed:bg-red-300 forced-colors:group-invalid:group-pressed:bg-[Mark]",
    ].join(" "),
    cap: "bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 group-invalid:bg-red-600 forced-colors:bg-[Highlight] forced-colors:group-invalid:bg-[Mark] forced-colors:text-white",
  };

  const disabledClasses = isDisabled ? "text-textFaint dark:text-zinc-600 forced-colors:text-textFaint font-light" : "";

  return [baseClasses, selectionStateClasses[selectionState], disabledClasses].filter(Boolean).join(" ");
}

export function RangeCalendar<T extends DateValue>({ errorMessage, ...props }: RangeCalendarProps<T>) {
  return (
    <AriaRangeCalendar {...props}>
      <CalendarHeader />
      <CalendarGrid className="w-full border-separate [border-spacing:0] [&_td]:p-0 [&_td]:!border-0">
        <CalendarGridHeader />
        <CalendarGridBody>
          {(date) => (
            <CalendarCell
              date={date}
              className="selection-start:rounded-s-full selection-end:rounded-e-full [td:last-child_&]:rounded-e-end group h-9 w-9 cursor-default text-sm border-0 outside-month:text-gray-300 invalid:selected:bg-red-100 forced-colors:selected:bg-[Highlight] forced-colors:selected:text-white forced-colors:invalid:selected:bg-[Mark] [td:first-child_&]:rounded-s-full [&]:border-0"
            >
              {({
                formattedDate,
                isSelected,
                isSelectionStart,
                isSelectionEnd,
                isFocusVisible: _isFocusVisible,
                isDisabled,
              }) => (
                <span
                  className={getCellClassName({
                    selectionState:
                      isSelected && (isSelectionStart || isSelectionEnd) ? "cap" : isSelected ? "middle" : "none",
                    isDisabled,
                  })}
                >
                  {formattedDate}
                </span>
              )}
            </CalendarCell>
          )}
        </CalendarGridBody>
      </CalendarGrid>
      {errorMessage && (
        <Text slot="errorMessage" className="text-sm text-red-600">
          {errorMessage}
        </Text>
      )}
    </AriaRangeCalendar>
  );
}
